// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { MyAccountPageContext } from "@insite/content-library/Pages/MyAccountPage";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadOrderTrackerData from "@mcmc/App/Handlers/MyAccount/LoadOrderTrackerData";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getOrderTrackerData } from "@mcmc/App/Store/Selectors/OrderTrackerDataSelectors";
import { styledBox } from "@mcmc/XDL/Components/Common/Box";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import { noResultsMessage, spinner } from "@mcmc/XDL/Utilities/sharedStyles";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: McMcApplicationState) => ({
    orderTrackerData: getOrderTrackerData(state),
    orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
});

const mapDispatchToProps = {
    loadOrderTrackerData,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderTrackerDataStyles {
    mainWrapper?: InjectableCss;
    dataTableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    tableHead?: DataTableHeadProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    statusHeaderCell?: DataTableCellBaseProps;
    centeringWrapper?: InjectableCss;
    orderNumberCell?: DataTableCellBaseProps;
    statusCell?: DataTableCellBaseProps;
}

export const orderTrackerDataStylesStyles: OrderTrackerDataStyles = {
    dataTableWrapper: {
        css: css`
            overflow-x: auto;
            position: relative;
            padding-top: 3px;
        `,
    },
    mainWrapper: {
        css: css`
            ${styledBox};
            margin-bottom: 0;
        `,
    },

    tableHead: {
        css: css`
            justify-content: center;
        `,
    },

    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 300px;
        `,
    },
    orderNumberCell: {
        css: css`
            height: 40px;
        `,
    },
    statusHeaderCell: { alignX: "center" },
    statusCell: {
        alignX: "center",
        css: css`
            height: 40px;
        `,
    },
};

const styles = orderTrackerDataStylesStyles;

const OrderTracker: React.FC<Props> = ({ orderTrackerData, loadOrderTrackerData, orderHistoryPageLink }) => {
    useEffect(() => {
        if (!orderTrackerData) {
            loadOrderTrackerData({});
        }
    }, []);

    const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

    return (
        <>
            <StyledWrapper {...styles.mainWrapper}>
                <CardListHeading heading={translate("Order Tracker")} viewAllUrl={orderHistoryUrl} />
                {(!orderTrackerData || orderTrackerData.length === 0) && (
                    <Typography {...noResultsMessage}>
                        <EllipsisSpinner
                            label={!orderTrackerData ? translate("Loading") : translate("No Items Available.")}
                            showSpinner={!orderTrackerData}
                            typographyProps={spinner}
                        />
                    </Typography>
                )}
                <StyledWrapper {...styles.dataTableWrapper}>
                    {orderTrackerData && orderTrackerData.length > 0 && (
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.tableHead}>
                                <DataTableHeader className="no-sticky">{translate("Order Number")}</DataTableHeader>
                                <DataTableHeader {...styles.statusHeaderCell}>
                                    {translate("Order Status")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.tableBody}>
                                {orderTrackerData.map(order => (
                                    <DataTableRow key={order.erpOrderNumber} {...styles.tableRow}>
                                        <DataTableCell {...styles.orderNumberCell} className="no-sticky">
                                            <OrderDetailPageTypeLink
                                                title={order.erpOrderNumber}
                                                orderNumber={order.erpOrderNumber}
                                            />
                                        </DataTableCell>
                                        <DataTableCell {...styles.statusCell}>{order.status}</DataTableCell>
                                    </DataTableRow>
                                ))}
                            </DataTableBody>
                        </DataTable>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const orderTrackerWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderTracker),
    definition: {
        allowedContexts: [MyAccountPageContext],
        group: "My Account",
    },
};

export default orderTrackerWidget;
